import { Close, Download, Error, QuestionMark } from '@mui/icons-material';
import {
  Badge,
  CircularProgress,
  Divider,
  Grid,
  Icon,
  List,
  ListItem,
  ListItemText,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
  styled,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchJobsInput } from 'switch-shared/modules/crud/job/job.dto';
import { Operation } from 'switch-shared/modules/crud/job/job.enums';
import { OrderByDir } from 'switch-shared/types/filter';
import { STATUS } from 'switch-shared/types/general.enums';

import { onOpenDownloadsPanel } from '../../utils/downloadsPanel';
import IconButton from '../Buttons/IconButton';
import InfoTooltip from '../Tooltips/InfoTooltip';

/** Milliseconds between job list refreshes while a download is pending. */
const POLL_INTERVAL_MS = 7500;

/** A download job as either console receives it; dates may be ISO strings. */
export interface DownloadJob {
  _id: string;
  status: STATUS;
  error?: string;
  createdAt?: Date | string;
  metadata?: {
    filename?: string;
    filetype?: string;
    downloaded?: boolean;
  };
}

/** Whether the download job's file is still being built. */
const isGenerating = (job: DownloadJob) =>
  job.status === STATUS.CREATING || job.status === STATUS.PENDING;

/** The job search hook each console supplies (its own RTK query). */
export type UseSearchJobsHook = (
  args: SearchJobsInput,
  options: { skip: boolean }
) => {
  data?: { data: DownloadJob[] };
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => void;
};

interface DownloadsTrayProps {
  /** Scope for the job search; the tray button is disabled when absent. */
  applicationGroupId?: string;

  useSearchJobs: UseSearchJobsHook;

  /** Fetches the job's file and hands it to the browser. */
  fetchDownload: (job: DownloadJob) => Promise<void>;
}

const DownloadsBadge = styled(Badge)(
  ({ theme }) => `
		.MuiBadge-badge {
				background-color: ${alpha(theme.palette.error.main, 0.1)};
				color: ${theme.palette.error.main};
				min-width: 16px;
				height: 16px;
				padding: 0;

				&::after {
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						border-radius: 50%;
						box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
						content: '';
				}
		}
`
);

/**
 * Nav-bar button and popover listing the download jobs of the last two days,
 * with a badge counting those that are pending or undownloaded.
 */
const DownloadsTray = ({
  applicationGroupId,
  useSearchJobs,
  fetchDownload,
}: DownloadsTrayProps) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => onOpenDownloadsPanel(() => setIsOpen(true)), []);

  const {
    data: downloads,
    isLoading,
    isSuccess,
    refetch,
  } = useSearchJobs(
    {
      applicationGroupId,
      operations: [Operation.Download],
      orderByField: 'createdAt',
      orderByValue: OrderByDir.Desc,
      limit: 1000,
      startTimeISO: dayjs().startOf('day').subtract(2, 'days').toISOString(),
    },
    { skip: !applicationGroupId }
  );

  useEffect(() => {
    const timer = setTimeout(() => setTick(tick + 1), POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [tick]);

  useEffect(() => {
    if (downloads?.data.some(isGenerating)) {
      refetch();
    }
  }, [tick, refetch, downloads?.data]);

  const pendingDownloads = useMemo(
    () =>
      downloads?.data.filter(
        (job) => isGenerating(job) || !job.metadata?.downloaded
      ) ?? [],
    [downloads]
  );

  const handleDownload = (job: DownloadJob) => {
    setTimeout(() => refetch(), 2500);
    void fetchDownload(job);
  };

  const getDescription = (job: DownloadJob) => {
    let status = 'Unknown';
    switch (job.status) {
      case STATUS.CREATING:
      case STATUS.PENDING:
        status = 'Pending';
        break;
      case STATUS.SUCCESS:
        status = job.metadata?.downloaded ? 'Downloaded' : 'Complete';
        break;
      case STATUS.ERROR:
        status = 'Error';
        break;
      default:
        break;
    }

    return `${dayjs(job.createdAt).format('DD/MM/YYYY, HH:mm')} - ${status}`;
  };

  const getSecondaryAction = (job: DownloadJob) => {
    switch (job.status) {
      case STATUS.CREATING:
      case STATUS.PENDING:
        return (
          <Tooltip title={'Generating...'}>
            <span>
              <CircularProgress size={'25px'} />
            </span>
          </Tooltip>
        );
      case STATUS.ERROR:
        return (
          <Tooltip title={job.error ?? 'Unknown error'}>
            <span>
              <Icon>
                <Error color='error' />
              </Icon>
            </span>
          </Tooltip>
        );
      case STATUS.SUCCESS:
        return (
          <Tooltip
            title={
              job.metadata?.downloaded
                ? 'Already downloaded. Download again?'
                : 'Ready for download'
            }
          >
            <IconButton onClick={() => handleDownload(job)}>
              <Download
                color={job.metadata?.downloaded ? 'disabled' : 'primary'}
              />
            </IconButton>
          </Tooltip>
        );
      default:
        return (
          <Tooltip title={'Unknown status'}>
            <span>
              <Icon>
                <QuestionMark color='disabled' />
              </Icon>
            </span>
          </Tooltip>
        );
    }
  };

  return (
    <>
      <Tooltip arrow title='Downloads'>
        <span>
          <IconButton
            ref={ref}
            aria-label='Downloads'
            onClick={() => setIsOpen(true)}
            disabled={!applicationGroupId}
            sx={{ opacity: !applicationGroupId ? 0.5 : 1 }}
          >
            <DownloadsBadge
              badgeContent={pendingDownloads.length}
              color='primary'
            >
              <Download color='action' />
            </DownloadsBadge>
          </IconButton>
        </span>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={() => setIsOpen(false)}
        open={isOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { style: { maxWidth: 500, width: '50%' } } }}
      >
        <Grid container alignItems={'center'} p={1} spacing={1} pl={2}>
          <Grid item container xs alignItems={'center'}>
            <Grid item>
              <Typography variant='h4'>
                <strong>Downloads</strong>
              </Typography>
            </Grid>
            <Grid item>
              <InfoTooltip
                placement='bottom'
                title='Downloads will be available for 2 days'
              />
            </Grid>
          </Grid>

          <Grid item>
            <IconButton onClick={() => setIsOpen(false)}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
        <Divider />
        <List>
          {isLoading ? (
            <ListItem sx={{ p: 0, px: 3 }}>
              <Skeleton width={'100%'} height={100} />
            </ListItem>
          ) : isSuccess && downloads?.data.length ? (
            downloads.data.map((job) => (
              <ListItem
                key={job._id}
                secondaryAction={
                  <Grid container justifyContent={'center'} width={50}>
                    <Grid item>{getSecondaryAction(job)}</Grid>
                  </Grid>
                }
              >
                <ListItemText
                  primary={job.metadata?.filename ?? 'Unknown download'}
                  secondary={getDescription(job)}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText primary='You currently have no downloads, check back later.' />
            </ListItem>
          )}
        </List>
      </Popover>
    </>
  );
};

export default DownloadsTray;
